import React, { useEffect, useRef } from "react";

import {
  ENTRY_DATA_SECTION_KEYS,
  LICENSE_AGREEMENT_GROUP_SHELL_KEYS,
} from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import { TableCreatorGroupShellTitle } from "@FillInTableContainer/shared";
import TableDragDivider from "@FillInTableContainer/shared/components/TableDragDivider/TableDragDivider";
import { useHandleTableCreatorSubGroupShellDrag } from "@FillInTableContainer/shared/hooks/useHandleTableCreatorSubGroupShellDrag";
import {
  GroupTableKeyType,
  TableCreatorSubGroupShellList,
} from "@FillInTableContainer/shared/types/tableCreator.types";
import { isAddDeleteGroupShell } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import {
  getAddedGroupShellTitle,
  getGroupShellTitle,
} from "@FillInTableContainer/shared/utils/getTextFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { newArrayToCountValue } from "@utils/newArrayToCountValue";

import DealLaScRightsScopeSubGroupShell from "./components/DealLaScRightsScopeSubGroupShell/DealLaScRightsScopeSubGroupShell";
import * as S from "./DealLaScRightsScopeGroupShell.style";
import useCheckIsDisplaySubGroupShellDragDivider from "./hooks/useCheckIsDisplaySubGroupShellDragDivider";
import { useGetNumberOfSupGroupShell } from "./hooks/useGetNumberOfSubGroupShell";
import { useHandleDealLaScRightsScopeTableCreatorGroupShell } from "./hooks/useHandleDealLaScRightsScopeTableCreatorGroupShell";

interface Props {
  groupTableKey: DealLaScSection;
  groupTableIndex: number;
  groupShellSectionKey: string | null;
  groupShellSectionIndex: number | null;
  groupShellKey: string | null;
  subGroupShellList: TableCreatorSubGroupShellList;
  tableKey: string | null;
  tableIndex: number;
  groupShellIndex: number;
  groupShellCount: number;
  handleDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => void;
  handleDragEnd: (groupTableKey: GroupTableKeyType) => void;
  handleDragOn: () => void;
  isDraggableGroupShell: boolean;
  isFocusGroupShellContainer: boolean;
}

const isNotFocusGroupShell: string[] = [
  LICENSE_AGREEMENT_GROUP_SHELL_KEYS.financialTerms,
];
const dragSubGroupShellList: string[] = ["Rights Scope"];
const ARTICLE_SECTION = ENTRY_DATA_SECTION_KEYS.dealsLaSc;

const DealLaScRightsScopeGroupShell = ({
  groupShellCount,
  groupShellIndex,
  groupShellKey,
  groupTableIndex,
  groupTableKey,
  groupShellSectionKey,
  groupShellSectionIndex,
  subGroupShellList,
  tableIndex,
  tableKey,
  handleDragEnd,
  handleDragOn,
  handleDragStart,
  isDraggableGroupShell,
  isFocusGroupShellContainer,
}: Props) => {
  const positionPropsInfo = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    groupShellSectionKey,
    groupShellSectionIndex,
    tableKey,
    tableIndex,
  };
  const articleSection = ARTICLE_SECTION;
  const { subGroupShellCount } = useGetNumberOfSupGroupShell(articleSection);
  const selectButtonRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const {
    focusStatus,
    currentInfoStatus,
    isFocused,
    isClearGroupShell,
    isDeleteGroupShell,
    isAlertGroupShell,
    isEnableEdit,
    handleFocusGroupShell,
    handleClickToDeleteGroupShell,
    handleDefaultFocus,
    changeFocusStatus,
    changePositionInfo,
    changePositionInfoToDefault,
  } = useHandleDealLaScRightsScopeTableCreatorGroupShell({
    positionPropsInfo,
    groupShellCount,
  });

  const {
    handleSubGroupShellDragEnd,
    handleSubGroupShellDragOn,
    handleSubGroupShellDragOver,
    handleSubGroupShellDragStart,
    subGroupShellDraggedIndexInfo,
    isDraggingSubGroupShell,
  } = useHandleTableCreatorSubGroupShellDrag(ARTICLE_SECTION, tableIndex);

  const { isLastSubGroupShell, handleCheckDragDividerDisplay } =
    useCheckIsDisplaySubGroupShellDragDivider({
      tableIndex,
      groupShellIndex,
      isDraggingSubGroupShell,
    });

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-dropdown="list-box"]'))
        return;
      if (isFocused) {
        const target = e.target as HTMLElement;

        if (target.closest("[data-button]")) return;
        if (target.dataset.button) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleDefaultFocus, isDeleteGroupShell, isFocused]);

  const isPossibleSelected = groupShellKey
    ? !isNotFocusGroupShell.includes(groupShellKey)
    : true;

  const checkIsDisplayDragDividerSubGroupShell = (
    subGroupShellKey: string | null,
  ) => {
    if (!subGroupShellKey) return false;

    return dragSubGroupShellList.includes(subGroupShellKey || "");
  };

  return (
    <>
      <S.GroupShellOutsideWrapper>
        {isClearGroupShell && isEnableEdit && (
          <S.FixedDeleteButtonWrapper
            isShellTitle={!!getGroupShellTitle(articleSection, groupShellKey)}
          >
            <S.DeleteTableButtonWrapper>
              <S.DeleteTableButton
                data-button="delete-button"
                onClick={handleClickToDeleteGroupShell}
              >
                <img src={IconXMark} alt="button for clear Row" />
                {focusStatus.type === "clear" ? `Clear` : null}
              </S.DeleteTableButton>
            </S.DeleteTableButtonWrapper>
          </S.FixedDeleteButtonWrapper>
        )}

        <S.GroupShellWrapper
          isFocused={isFocused}
          isClearGroupShell={isClearGroupShell}
          isDeleteGroupShell={isDeleteGroupShell}
          isAlertGroupShell={isAlertGroupShell}
          isDragging={isFocused && focusStatus.type === "dragging"}
        >
          <S.ContentsWrapper>
            <S.TitleWrapper ref={titleRef} data-button="group-shell-title">
              {getAddedGroupShellTitle(articleSection, groupShellKey) && (
                <TableCreatorGroupShellTitle
                  articleSectionKey={articleSection}
                  groupShellKey={groupShellKey}
                  groupShellCount={groupShellCount}
                  groupShellIndex={groupShellIndex}
                  isAlertGroupShell={isAlertGroupShell}
                  onClickHandler={() => {
                    handleFocusGroupShell();
                  }}
                  groupTableKey={groupTableKey}
                  handleDragStart={handleDragStart}
                  handleDragEnd={handleDragEnd}
                  handleDragOn={handleDragOn}
                  isDragging={isFocused && focusStatus.type === "dragging"}
                  isDraggableGroupShell={isDraggableGroupShell}
                  changeFocusStatusForDragging={() => {
                    changePositionInfo();
                    changeFocusStatus({
                      type: "dragging",
                      container: "groupShell",
                    });
                  }}
                  changeFocusStatusForDragEnd={() => {
                    changePositionInfoToDefault();
                    changeFocusStatus({
                      type: "default",
                      container: "noFocus",
                    });
                  }}
                  customGroupShellTitle="Rights Scope"
                />
              )}
            </S.TitleWrapper>
            <S.GroupShellHeaderWrapper>
              <S.GroupShellHeader minWidth="12rem" maxWidth="12rem">
                Asset
              </S.GroupShellHeader>
              <S.GroupShellHeader minWidth="12rem" maxWidth="12rem">
                Type
              </S.GroupShellHeader>
              <S.GroupShellHeader minWidth="14.4rem" maxWidth="14.4rem">
                Rights Scope
              </S.GroupShellHeader>
              <S.GroupShellHeader minWidth="8.8rem" maxWidth="9.6rem">
                Rights Flow
              </S.GroupShellHeader>
              <S.GroupShellHeader minWidth="12.8rem" maxWidth="12.8rem">
                Rights Limitation
              </S.GroupShellHeader>
              <S.GroupShellHeader minWidth="12rem" maxWidth="12rem">
                Intended Use
              </S.GroupShellHeader>

              <S.GroupShellHeader minWidth="12rem" maxWidth="12rem">
                Exclusivity
              </S.GroupShellHeader>
              <S.GroupShellHeader minWidth="12rem" maxWidth="12rem">
                Territory
              </S.GroupShellHeader>
              <S.GroupShellHeader minWidth="12.8rem" maxWidth="12.8rem">
                Include
              </S.GroupShellHeader>
              <S.GroupShellHeader minWidth="12.8rem" maxWidth="12.8rem">
                Exclude
              </S.GroupShellHeader>
            </S.GroupShellHeaderWrapper>
            <S.SelectWrapper
              $isAddedGroupShell={isAddDeleteGroupShell(
                articleSection,
                groupShellKey,
              )}
            >
              <S.GroupShellSelectButtonDividerWrapper
                ref={selectButtonRef}
                data-button="group-shell-select"
                onClick={handleFocusGroupShell}
                isPossibleSelected={isPossibleSelected}
                isFinancialTerms
              >
                <S.GroupShellSelectButtonDivider className="select-button divider" />
                <S.GroupShellSelectButton className="select-button button">
                  SELECT
                </S.GroupShellSelectButton>
              </S.GroupShellSelectButtonDividerWrapper>

              <S.GroupShell>
                {subGroupShellList.map(
                  ({ subGroupShellKey, shellList }, shellListIndex) => (
                    <React.Fragment key={`shellList-${String(shellListIndex)}`}>
                      {newArrayToCountValue(
                        subGroupShellCount(
                          subGroupShellKey,
                          tableIndex,
                          groupShellIndex,
                        ),
                      ).map((_, subGroupShellIndex) => (
                        <S.DraggableSubGroupShellWrapper
                          isTargetDraggingTable={
                            subGroupShellKey ===
                              currentInfoStatus.subGroupShellKey &&
                            isDraggingSubGroupShell &&
                            subGroupShellDraggedIndexInfo[0] !==
                              subGroupShellIndex &&
                            checkIsDisplayDragDividerSubGroupShell(
                              subGroupShellKey,
                            )
                          }
                          onDragOver={(e) =>
                            handleSubGroupShellDragOver(e, subGroupShellIndex)
                          }
                        >
                          {checkIsDisplayDragDividerSubGroupShell(
                            subGroupShellKey,
                          ) && (
                            <TableDragDivider
                              isDraggingEndPosition={
                                subGroupShellKey ===
                                  currentInfoStatus.subGroupShellKey &&
                                handleCheckDragDividerDisplay({
                                  moveDragIndex:
                                    subGroupShellDraggedIndexInfo[1],
                                  startDragIndex:
                                    subGroupShellDraggedIndexInfo[0],
                                  subGroupShellIndex,
                                  subGroupShellKey,
                                })
                              }
                              style={{
                                margin: "0",
                                padding: "0",
                                position: "absolute",
                                top: "-1px",
                                left: "3.6rem",
                                height: "0.2rem",
                                width: "calc(100% - 3.6rem)",
                                zIndex: "100",
                              }}
                            />
                          )}
                          <DealLaScRightsScopeSubGroupShell
                            key={`shell-${String(groupTableIndex)}-${String(
                              tableIndex,
                            )}-${String(groupShellIndex)}-${String(
                              subGroupShellIndex,
                            )}`}
                            groupTableKey={groupTableKey}
                            groupTableIndex={groupTableIndex}
                            groupShellKey={groupShellKey}
                            groupShellIndex={groupShellIndex}
                            subGroupShellKey={subGroupShellKey}
                            shellList={shellList}
                            subGroupShellIndex={subGroupShellIndex}
                            tableKey={tableKey}
                            tableIndex={tableIndex}
                            subGroupShellCount={subGroupShellCount(
                              subGroupShellKey,
                              tableIndex,
                              groupShellIndex,
                            )}
                            isFocusedGroupShell={isFocused}
                            isClearGroupShell={isClearGroupShell}
                            isFocusGroupShellContainer={
                              isFocusGroupShellContainer
                            }
                            isDeleteGroupShell={isDeleteGroupShell}
                            isAlertGroupShell={isAlertGroupShell}
                            handleDragEnd={handleSubGroupShellDragEnd}
                            handleDragOn={handleSubGroupShellDragOn}
                            handleDragStart={handleSubGroupShellDragStart}
                          />
                          {checkIsDisplayDragDividerSubGroupShell(
                            subGroupShellKey,
                          ) &&
                            isLastSubGroupShell(
                              subGroupShellIndex,
                              subGroupShellKey,
                            ) && (
                              <TableDragDivider
                                isDraggingEndPosition={
                                  subGroupShellKey ===
                                    currentInfoStatus.subGroupShellKey &&
                                  handleCheckDragDividerDisplay({
                                    moveDragIndex:
                                      subGroupShellDraggedIndexInfo[1],
                                    startDragIndex:
                                      subGroupShellDraggedIndexInfo[0],
                                    subGroupShellIndex,
                                    subGroupShellKey,
                                    lastIndex: newArrayToCountValue(
                                      subGroupShellCount(
                                        subGroupShellKey,
                                        tableIndex,
                                        groupShellIndex,
                                      ),
                                    ).length,
                                  })
                                }
                                style={{
                                  margin: "0",
                                  padding: "0",
                                  position: "absolute",
                                  bottom: "-1px",
                                  left: "3.6rem",
                                  height: "0.2rem",
                                  width: "calc(100% - 3.6rem)",
                                  zIndex: "100",
                                }}
                              />
                            )}
                        </S.DraggableSubGroupShellWrapper>
                      ))}
                    </React.Fragment>
                  ),
                )}
              </S.GroupShell>
            </S.SelectWrapper>
          </S.ContentsWrapper>
        </S.GroupShellWrapper>
      </S.GroupShellOutsideWrapper>
    </>
  );
};

export default DealLaScRightsScopeGroupShell;
